/**
 * guidesModel.ts — the pure judgements behind the dashboard "Your guides"
 * card, kept out of the component so they can be tested without a DOM (the
 * journeyModel precedent).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement unlock (2026-09-23).
 *
 * THE CARD IS A VIEW UNTIL THE SERVER SAYS UNLOCKED. `GET …/engagement/mine`
 * answers the SAME server-side ladder as the public /unlock resolve — one
 * derivation, never re-derived here — and no file leaves before the server
 * says `unlocked` (the mine/download route re-derives on every click anyway;
 * R41, the UI is never the control).
 *
 * WHAT RENDERS NOTHING, AND WHY EACH ONE DOES:
 *  - `state:'none'` or an empty item list — nothing has been sent, so a card
 *    would be an empty promise on the patient's home;
 *  - a read still in flight, or a FAILED read — the card asserts nothing
 *    about the patient's obligations, so its absence claims nothing either
 *    (unlike the evidence lane, where a failed read must be SAID — a missing
 *    guides card is not a false all-clear about anything);
 *  - a state this build has not heard of — rendering the locked framing
 *    would claim "unlocks with your plan" about a state we cannot read.
 *
 * WHY `waiting` GETS THE TILES BUT NOT THE BANNER. The banner's whole
 * sentence is "finish setting it up", and a `waiting` patient has NOTHING to
 * finish — their answers are with the clinician and we owe them the next
 * move. Telling them to act would be the 2026-09-05 hero defect inverted
 * (inventing an obligation instead of hiding one). The tiles still show, with
 * the card's own "they unlock with your plan" framing, because the guides ARE
 * coming and hiding them would un-say what the unlock email already said.
 *
 * WHY `lapsed` IS NOT "waiting" EITHER. A lapsed plan is OVER, so the
 * plan-waiting banner's copy — "Your plan is waiting… about 2 minutes" —
 * would be FALSE of it: there is nothing to finish, only a plan to restart.
 * It gets its own honest banner variant (`plan_ended`), mirroring the
 * /unlock page's own LapsedView framing ("Your plan has ended" / restart).
 */
import type { MyEngagement, UnlockItem, UnlockState } from '../api/types';

/** Which banner (if any) a locked card carries — never one banner for all. */
export type GuidesBanner = 'plan_waiting' | 'plan_ended';

export type GuidesView =
  | { kind: 'hidden' }
  | { kind: 'unlocked'; items: UnlockItem[] }
  | { kind: 'locked'; items: UnlockItem[]; banner: GuidesBanner | null };

/** The locked states with a PENDING STEP the waiting banner can honestly
 * point at — a step that is genuinely the PATIENT'S to finish. `lapsed` is
 * deliberately NOT here (see the header); `waiting` never was. */
const PLAN_WAITING_STATES: ReadonlySet<UnlockState> = new Set(['needs_plan', 'needs_questionnaire']);
/** Every locked state whose tiles still render (the VIEW, per the board). */
const LOCKED_STATES: ReadonlySet<UnlockState> = new Set(['needs_plan', 'needs_questionnaire', 'lapsed', 'waiting']);

function bannerFor(state: UnlockState): GuidesBanner | null {
  if (PLAN_WAITING_STATES.has(state)) return 'plan_waiting';
  if (state === 'lapsed') return 'plan_ended'; // ended, not waiting — honest copy or none
  return null; // `waiting` — nothing is the patient's to finish
}

export function guidesView(mine: MyEngagement | null | undefined): GuidesView {
  if (!mine) return { kind: 'hidden' }; // in flight, failed, or absent — claim nothing
  if (mine.items.length === 0) return { kind: 'hidden' }; // none / nothing sent yet
  if (mine.state === 'unlocked') return { kind: 'unlocked', items: mine.items };
  if (LOCKED_STATES.has(mine.state)) {
    return { kind: 'locked', items: mine.items, banner: bannerFor(mine.state) };
  }
  return { kind: 'hidden' }; // `none` with items, or an unknown future state
}
